/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { SecurityHelper } from 'sarsaparilla';
import axios from 'axios';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import { Action, Dispatch } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { type AuthSession } from 'shared-ui/auth/redux';

import * as types from '../constants';
import { navigateToSearchResults, updateSearchCriterias } from './search';
import { FLAT_CAMPSITE_FILTERS } from '../constants/filters/camping';
import { getPreferencesPayload } from '../utils/tripPreferences';
import { RootState } from '../../dev/store';
import { TripPreferenceType } from '../types/tripPreferences';
import { SearchType } from '../types/search';

export const setPreferencesSucceeded = (preferences?: Partial<TripPreferenceType>) => ({
    type: types.PREFERENCES_UPDATE_SUCCESS,
    payload: preferences,
});

export const updatePreferencesFailed = () => ({
    type: types.PREFERENCES_UPDATE_FAILED,
});

/**
 * Filters preferences to only include valid fg filter keys
 */
export const getValidPreferences = (data: string[]) => {
    const validKeys = FLAT_CAMPSITE_FILTERS.map((item) => item.key);
    return data?.filter((item: string) => validKeys.some((key) => item.includes(key)));
};

/**
 * Fetches trip preferences for a given account
 */
export const getTripPreferences: (
    account: AuthSession | null
) => (dispatch: Dispatch<Action>) => Promise<string[]> =
    (account: AuthSession | null) => (dispatch: Dispatch<Action>) =>
        new Promise((resolve, reject) => {
            const accountId = account?.account?.account_id;
            if (!accountId) {
                throw new Error('Account ID is required');
            }
            const url = `${process.env.API}/accounts/account/${accountId}/trip-preferences`;
            axios
                .get(url, {
                    headers: { Authorization: SecurityHelper.getAuthHeader() },
                })
                .then(async (response) => {
                    const preferences: string[] = (response?.data ?? []).map(
                        (item: { value: string }) => item.value
                    );
                    dispatch(
                        setPreferencesSucceeded({
                            preferences: getValidPreferences(preferences),
                            account_id: accountId,
                        })
                    );
                    resolve(preferences);
                })
                .catch(() => {
                    dispatch(updatePreferencesFailed());
                    reject({});
                });
        });

/**
 * Updates trip preferences for the current account
 */
export const updateTripPreferences =
    (preferences: string[]) => (dispatch: Dispatch<Action>, getState: () => RootState) =>
        new Promise(async (resolve, reject) => {
            const account = getState().auth.account;
            const accountId = account?.account?.account_id;

            if (!accountId) {
                throw new Error('Account ID is required');
            }

            const payload = {
                trip_preferences: getPreferencesPayload(preferences),
                account_id: accountId,
            };
            const url = `${process.env.API}/accounts/account/trip-preferences`;
            axios
                .put(url, payload, {
                    headers: { Authorization: SecurityHelper.getAuthHeader() },
                })
                .then(() => {
                    const localValue = {
                        preferences: preferences ?? [],
                        account_id: accountId,
                    };
                    resolve(preferences);
                    dispatch(setPreferencesSucceeded(localValue));
                })
                .catch((e) => {
                    dispatch(updatePreferencesFailed());
                    reject({});
                    throw e;
                });
        });

/**
 * Saves trip preferences for the current account. If they're applied to the
 * search, swaps the old ones in fg for the new ones and re-runs the search
 */
export const saveTripPreferences =
    (preferences: string[]) =>
    async (
        dispatch: ThunkDispatch<RootState, unknown, Action<string>>,
        getState: () => RootState
    ) => {
        // Read before saving, since saving replaces them in the store
        const previousPreferences: string[] = get(
            getState(),
            'tripPreferences.preferences',
            []
        );

        await dispatch(updateTripPreferences(preferences));

        const search: SearchType = get(getState(), 'search', {});
        if (!search.tripPreferences) {
            return;
        }

        const otherFilters = get(search, 'fg', []).filter(
            (item) => previousPreferences.indexOf(item) === -1
        );
        dispatch(
            updateSearchCriterias({
                fg: uniq([...otherFilters, ...preferences]),
                tripPreferences: preferences.length > 0,
            })
        );
        dispatch(navigateToSearchResults());
    };

/**
 * Disables trip preferences and updates search criteria
 */
export const disableTripPreferences =
    () =>
    async (
        dispatch: ThunkDispatch<RootState, unknown, Action<string>>,
        getState: () => RootState
    ) => {
        const state = getState();
        const search: SearchType = get(state, 'search', {});
        const preferences: string[] = get(state, 'tripPreferences.preferences', []);

        if (search.tripPreferences) {
            const currentFilters = [...get(search, 'fg', [])];
            const updatedFilters = currentFilters.filter(
                (item) => preferences.indexOf(item) === -1
            );
            dispatch(
                updateSearchCriterias({
                    fg: uniq(updatedFilters),
                    tripPreferences: false,
                })
            );

            // Search in the same tick as the update, so the results count never shows
            // the old total in between
            dispatch(navigateToSearchResults());
        }
    };

export const applyTripPreferences =
    () =>
    async (
        dispatch: ThunkDispatch<RootState, unknown, Action<string>>,
        getState: () => RootState
    ) => {
        const state = getState();

        // Get current search state and account data
        const search: any = get(state, 'search', {});
        const account = state.auth.account;

        // Fetch and apply preferences
        const preferences = await dispatch(getTripPreferences(account));
        const fg = [...get(search, 'fg', []), ...(preferences ?? [])];
        dispatch(
            updateSearchCriterias({
                fg: uniq(fg),
                tripPreferences: true,
            })
        );

        // Search in the same tick as the update, so the results count never shows
        // the old total in between
        dispatch(navigateToSearchResults());
    };
